import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';
import { useAtomValue } from 'jotai';

import { mediaQuery } from '@/theme/setupTheme';

import SavvyBetaLogo from '@/assets/svg/ai-enhancement-savvy-beta.svg';
import { useProject } from '@/features/projects/overview/use-project.query';
import { projectIdState } from '@/features/projects/project.state';
import { Button } from '@/ui/button/button';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Stack } from '@/ui/line/line';
import { Modal as ModalComponent } from '@/ui/modal/modal';
import { PRODUCT } from '@/user/products/product.type';
import { useUserProducts } from '@/user/products/use-user-products';
import { draftJsRawToPlainText } from '@/helpers/draftjs';

import { TEXTAREA_MAX_LENGTH } from '@/features/enhance-common/constants/enhance.constants';
import { StepText } from '@/features/enhance-common/styles/enhance-modal.styles';
import { EnhanceHeader } from '@/features/enhance-common/components/enhance-header';
import { AnalyzingView } from './views/analyzing-view';
import { useProjectListingSuggestionState } from './use-project-listing-suggestion.state';
import { useCreateProjectListingSuggestions } from './use-create-project-listing-suggestion.mutation';
import type { WizardStep } from './use-project-listing-suggestion.state';
import { CompanySelectionViewWrapper } from './views/company-selection-view-wrapper';
import { ProblemDefinitionViewWrapper } from './views/problem-definition-view-wrapper';

const WIZARD_STEPS = {
  ANALYZING: 'analyzing',
  COMPANY_SELECTION: 'companySelection',
  ERROR: 'error',
  PROBLEM_DEFINITION: 'problemDefinition',
  RESULTS: 'results',
} as const satisfies Record<string, WizardStep>;

const STEP_COMPONENTS: Record<WizardStep, React.ComponentType> = {
  analyzing: AnalyzingView,
  companySelection: CompanySelectionViewWrapper,
  error: AnalyzingView,
  problemDefinition: ProblemDefinitionViewWrapper,
  results: AnalyzingView,
};

export const ProjectListingSuggestionFormModal = () => {
  const { t } = useTranslation(['projects', 'default']);
  const { hasRequiredProduct } = useUserProducts();
  const state = useProjectListingSuggestionState();
  const { companyFilterSelected, setProblemStatement } = state;
  const projectId = useAtomValue(projectIdState);
  const { data } = useProject(projectId);

  const hasProjectListings = (state.projectListings?.length ?? 0) > 0;
  const hasInitializedCompaniesRef = useRef(false);

  const { mutate: triggerEnhance, isPending: isEnhancing } =
    useCreateProjectListingSuggestions(state.projectId || undefined);

  const unselectedCompanies = useMemo(
    () =>
      state.projectListings
        ?.filter(
          listing =>
            !state.companyFilterSelected.includes(listing.organization.id),
        )
        .map(listing => listing.organization.id),
    [state.projectListings, state.companyFilterSelected],
  );

  useEffect(() => {
    if (data?.problemStatement) {
      const plainText = draftJsRawToPlainText(data.problemStatement);
      setProblemStatement(plainText);
    }
  }, [data?.problemStatement, setProblemStatement]);

  useEffect(() => {
    state.setDesiredSolution('');
    state.setLocations([]);
    state.setCompanyFilterSort({ direction: 'desc', sort: 'status' });
    return () => {
      state.setCurrentPageStep(WIZARD_STEPS.PROBLEM_DEFINITION);
      state.setProblemStatement('');
      hasInitializedCompaniesRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (hasInitializedCompaniesRef.current) return;
    if (companyFilterSelected?.length > 0) return;

    if (hasProjectListings) {
      state.setCompanyFilterSelectAll(true);
      hasInitializedCompaniesRef.current = true;
    }
  }, [hasProjectListings, companyFilterSelected, state]);

  const isFormValid = useMemo(
    () =>
      state.problemStatement.length > 0 &&
      state.problemStatement.length <= TEXTAREA_MAX_LENGTH,
    [state.problemStatement],
  );

  const handleBack = useCallback(() => {
    if (state.currentPageStep === WIZARD_STEPS.COMPANY_SELECTION) {
      state.setCurrentPageStep(WIZARD_STEPS.PROBLEM_DEFINITION);
    } else if (state.currentPageStep === WIZARD_STEPS.RESULTS) {
      state.setCurrentPageStep(WIZARD_STEPS.COMPANY_SELECTION);
    }
  }, [state]);

  const handleNavigateButtonClick = useCallback(() => {
    const payload = {
      problemDescription: state.problemStatement,
      desiredSolution: state.desiredSolution,
      locations:
        state.locations.length > 0
          ? state.locations.map(location => JSON.parse(location.value))
          : undefined,
      filterCompanies:
        !!unselectedCompanies && unselectedCompanies.length > 0
          ? unselectedCompanies
          : undefined,
    };

    switch (state.currentPageStep) {
      case WIZARD_STEPS.PROBLEM_DEFINITION:
        if (hasProjectListings) {
          state.setCurrentPageStep(WIZARD_STEPS.COMPANY_SELECTION);
        } else {
          triggerEnhance(payload);
        }
        break;
      case WIZARD_STEPS.COMPANY_SELECTION:
      case WIZARD_STEPS.ERROR:
      case WIZARD_STEPS.RESULTS:
        void triggerEnhance(payload);
        break;
    }
  }, [state, hasProjectListings, triggerEnhance, unselectedCompanies, t]);

  const getButtonText = useCallback(() => {
    switch (state.currentPageStep) {
      case WIZARD_STEPS.PROBLEM_DEFINITION:
        return hasProjectListings
          ? t`enhanceConfigNext`
          : t`enhanceConfigEnhance`;
      case WIZARD_STEPS.COMPANY_SELECTION:
        return t`enhanceConfigEnhance`;
      case WIZARD_STEPS.ERROR:
        return t`enhanceRetry`;
      case WIZARD_STEPS.RESULTS:
        return t`enhanceRegenerate`;
      default:
        return t`enhanceConfigNext`;
    }
  }, [state.currentPageStep, hasProjectListings, t]);

  const getStepIndicator = useCallback(() => {
    if (state.currentPageStep === WIZARD_STEPS.PROBLEM_DEFINITION) {
      return hasProjectListings
        ? t`enhanceConfigStage1of2`
        : t`enhanceConfigStage1of1`;
    }
    if (state.currentPageStep === WIZARD_STEPS.COMPANY_SELECTION) {
      return t`enhanceConfigStage2of2`;
    }
    return null;
  }, [state.currentPageStep, hasProjectListings, t]);

  if (!data || !hasRequiredProduct(PRODUCT.ENHANCE_PROJECT)) return null;

  const showBackButton =
    state.currentPageStep === WIZARD_STEPS.COMPANY_SELECTION;
  const showHeader = state.currentPageStep !== WIZARD_STEPS.ANALYZING;
  const StepComponent = STEP_COMPONENTS[state.currentPageStep];
  const stepIndicator = getStepIndicator();

  return (
    <Modal
      CustomHeaderComponent={
        showHeader
          ? () => (
              <EnhanceHeader
                showBackButton={showBackButton}
                onBack={handleBack}
              />
            )
          : undefined
      }
      header={showHeader ? t`enhanceConfigHeader` : undefined}
      outsideClose={false}
    >
      <ModalContentStack data-testid="enhance-project-modal-content">
        <StepComponent />

        <Flexbox
          name="enhance-config-footer"
          justify="space-between"
          alignItems="center"
          padding="30px 0px"
          dataTestId="enhance-config-footer"
        >
          <img height="38px" src={SavvyBetaLogo} alt="savvy-beta-logo" />
          {showHeader && (
            <>
              {stepIndicator && (
                <StepText data-testid="enhance-step-indicator">
                  {stepIndicator}
                </StepText>
              )}
              <Button
                data-testid="project-listing-suggestion-next-button"
                contentPadding="lg"
                disabled={!isFormValid || isEnhancing}
                onClick={handleNavigateButtonClick}
              >
                {getButtonText()}
              </Button>
            </>
          )}
        </Flexbox>
      </ModalContentStack>
    </Modal>
  );
};

const ModalContentStack = styled(Stack)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
`;

const Modal = styled(ModalComponent)`
  justify-content: space-between;
  max-width: 100%;
  min-height: 648px;
  padding: 24px 24px 0px 24px;
  width: 95%;
  max-height: 90vh;
  overflow: hidden;

  ${mediaQuery('sm', 'min')} {
    padding: 32px 24px 0px 24px;
    width: 90%;
  }

  ${mediaQuery('md', 'min')} {
    max-width: 1250px;
    min-width: 600px;
    padding: 38px 50px 0px 50px;
    width: 85%;
  }

  ${mediaQuery('lg', 'min')} {
    max-width: 1250px;
    min-width: 1250px;
    width: 1250px;
  }
`;

export const OverflowContainer = styled(Flexbox)`
  overflow-y: auto;
  margin-top: 18px;
  padding-right: 10px;
  ${({ theme }) => theme.mixins.scrollbar}
`;
